import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Sticky-scroll for produktkolonnen.
 *
 * Horizon gør produktkolonnen sticky med `top: var(--sticky-header-offset)`
 * (`assets/base.css:1064-1069`). Er kolonnen højere end skærmen, klæber den fast
 * med det samme, og bunden - mængderabatten og købsknappen - kan først ses, når
 * hele billedgitteret er scrollet forbi.
 *
 * Fixet flytter klæbepunktet: `top` sættes til `skærmhøjde - kolonnehøjde - luft`,
 * altså et negativt tal. Så scroller kolonnen med som normalt, indtil dens bund
 * står nederst på skærmen, og først dér klæber den fast. Er kolonnen lavere end
 * skærmen, bliver tallet positivt, og så rører vi ikke ved Horizons opførsel.
 *
 * Værdien skrives som `--locktown-sticky-scroll-top` på `.product-details`.
 * Stylesheet'et i `blocks/locktown-sticky-scroll.liquid` falder tilbage til
 * `--sticky-header-offset`, hvis variablen mangler, så siden virker uden JS.
 *
 * Højden måles igen ved resize, ved ændringer i kolonnen (fx en accordion, der
 * foldes ud) og ved variantskift, hvor morph kan have fjernet variablen igen.
 */

/** Variablen stylesheet'et læser. */
const TOP_PROPERTY = '--locktown-sticky-scroll-top';

class LocktownStickyScroll extends Component {
  /** Produktkolonnen, vi styrer. @type {HTMLElement | null} */
  #details = null;

  /** @type {ResizeObserver | null} */
  #observer = null;

  /** @type {number | null} */
  #frame = null;

  /** Sektionen (eller dialogen ved quick-add), der sender variantskift. @type {Element | null} */
  #scope = null;

  connectedCallback() {
    super.connectedCallback();

    this.#details = this.#findDetails();
    if (!this.#details) return;

    this.#observer = new ResizeObserver(this.#schedule);
    this.#observer.observe(this.#details);

    window.addEventListener('resize', this.#schedule);

    this.#scope = this.closest('.shopify-section, dialog');
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onSelect);

    this.#measure();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#schedule);
    this.#scope?.removeEventListener(StandardEvents.productSelect, this.#onSelect);
    this.#scope = null;

    this.#observer?.disconnect();
    this.#observer = null;

    if (this.#frame != null) cancelAnimationFrame(this.#frame);
    this.#frame = null;

    this.#reset();
    this.#details = null;
  }

  /** Morph har ændret blockens eget indhold. */
  updatedCallback() {
    super.updatedCallback();
    this.#schedule();
  }

  /**
   * Produktkolonnen ligger som forælder, når blocken står inde i den. Står den
   * et andet sted i sektionen, findes kolonnen i sektionen i stedet.
   * @returns {HTMLElement | null}
   */
  #findDetails() {
    const parent = this.closest('.product-details');
    if (parent instanceof HTMLElement) return parent;

    const inSection = this.closest('.shopify-section, dialog')?.querySelector('.product-details');
    return inSection instanceof HTMLElement ? inSection : null;
  }

  /** Samler flere kald i én måling pr. frame. */
  #schedule = () => {
    if (this.#frame != null) return;

    this.#frame = requestAnimationFrame(() => {
      this.#frame = null;
      this.#measure();
    });
  };

  #measure() {
    const details = this.#details;
    if (!details) return;

    if (this.dataset.enabled !== 'true') {
      this.#reset();
      return;
    }

    const gap = Number(this.dataset.gap) || 0;
    const height = details.getBoundingClientRect().height;
    const top = this.#viewportHeight() - height - gap;

    // Kolonnen er lavere end skærmen: Horizons egen opførsel er den rigtige.
    if (height === 0 || top >= 0) {
      this.#reset();
      return;
    }

    details.style.setProperty(TOP_PROPERTY, `${Math.round(top)}px`);
  }

  #reset() {
    this.#details?.style.removeProperty(TOP_PROPERTY);
  }

  /**
   * Højden af det, sticky måler op imod. På desktop (>= 990px) scroller Horizon
   * ikke dokumentet, men `.page-wrapper` (`assets/base.css:47-52`,
   * `assets/scroll-container.js:1-17`). Så er det den, sticky forholder sig til.
   */
  #viewportHeight() {
    const wrapper = document.querySelector('.page-wrapper');

    if (wrapper instanceof HTMLElement && getComputedStyle(wrapper).overflowY !== 'visible') {
      return wrapper.clientHeight;
    }

    return window.innerHeight;
  }

  /**
   * Variantskift: den nye HTML morphes ind, og morph kan have fjernet
   * variablen fra `style`-attributten igen. Mål derfor forfra bagefter.
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onSelect = (event) => {
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    const done = event.promise;
    if (!done) {
      this.#schedule();
      return;
    }

    done
      .then(() => this.#schedule())
      .catch((error) => {
        if (error && typeof error === 'object' && 'name' in error && error.name === 'AbortError') return;
        console.warn(error);
      });
  };
}

if (!customElements.get('locktown-sticky-scroll-component')) {
  customElements.define('locktown-sticky-scroll-component', LocktownStickyScroll);
}
